/**
 * Tag data transformation and manipulation utility functions
 * Used by asset detail pages
 */

import { ENALYSIS_TREND_LIST_SOURCE } from "@/constants/enalysis-trends-constants.js";
import { arrayUtils } from "./utils.js";
import { removeCommasAndSpaces } from "./formatting-utils.js";

const getCurrentValue = (tag) => {
  if (tag.TagState === "NoData") {
    let lastReportingDate = new Date(tag.ReportingDate).toLocaleString(
      "en-US",
      {
        year: "2-digit",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }
    );
    // Format: "Jan 10, 25 11:25 PM" - remove comma before time and "at"
    lastReportingDate = lastReportingDate
      .replace(/,(?=\s*\d{1,2}:\d{2}\s*(?:AM|PM))/i, "")
      .replace(/\s+at\s+/i, " ");
    return tag.TagValueDisplay + " " + lastReportingDate;
  }
  return tag.TagValueDisplay;
};

/**
 * Transform API data to UI format for tag data
 * @param {Object} apiData - Data from getTagGroupsForAsset API
 * @returns {Object} Transformed data in UI format with dynamic categories
 */
export const transformApiDataToUIFormat = (apiData) => {
  if (!apiData || !apiData.Tags) {
    return {};
  }

  const { Tags, Groups } = apiData;

  // Create a map of tag groups for easy lookup
  const groupMap = {};
  if (Groups && arrayUtils.hasItems(Groups)) {
    Groups.forEach((group) => {
      groupMap[group.Category] = group;
    });
  }

  // Initialize transformed data with dynamic categories
  const transformedData = {};

  // Initialize each group category
  if (Groups && arrayUtils.hasItems(Groups)) {
    Groups.forEach((group) => {
      transformedData[group.Category] = [];
    });
  }
  transformedData["Individual Parameters"] = [];

  Tags.forEach((tag) => {
    const parameter = {
      parameter: tag.TagName,
      unit: tag.ShortUnitOfMeasure || tag.UnitOfMeasure || "",
      displayType: tag.DisplayType === 0 ? "Text" : "Gauge",
      universalDescriptorId:
        tag.UniversalDescriptorId || tag.universalDescriptorId || null,
      alarmSetPoints: {
        lolo: tag.AlarmLoLo,
        lo: tag.AlarmLo,
        hi: tag.AlarmHi,
        hihi: tag.AlarmHiHi,
      },
      status: tag.TagState,
      currentValue: getCurrentValue(tag),
      // Store additional API data for potential future use
      apiData: {
        assetId: tag.AssetId,
        assetTagId: tag.AssetTagId,
        reportingDate: tag.ReportingDate,
        displayType: tag.DisplayType,
        minValue: tag.MinValue,
        maxValue: tag.MaxValue,
      },
    };

    // Find all groups this tag belongs to
    const assignedGroups = [];
    for (const [groupCategory, group] of Object.entries(groupMap)) {
      if (group.AssetTagIDs && group.AssetTagIDs.includes(tag.AssetTagId)) {
        assignedGroups.push(groupCategory);
      }
    }

    if (assignedGroups.length === 0) {
      assignedGroups.push("Individual Parameters");
    }

    // Add parameter to all groups it belongs to
    assignedGroups.forEach((groupCategory) => {
      if (transformedData[groupCategory]) {
        transformedData[groupCategory].push(parameter);
      }
    });
  });

  return transformedData;
};

/**
 * Transform API data to chart format for trend popup
 * @param {Object} apiResponse - API response object
 * @param {string} selectedPeriod - Selected time period
 * @param {Function} formatTimeAxis - Function to format time axis
 * @returns {Array} Transformed data for chart
 */
export const transformApiDataToChartFormat = (
  apiResponse,
  selectedPeriod,
  formatTimeAxis
) => {
  if (
    !apiResponse
    || !apiResponse.Measurements
    || !Array.isArray(apiResponse.Measurements)
  ) {
    return [];
  }

  return apiResponse.Measurements.map((measurement) => {
    const rawText =
      measurement.TextValue !== undefined && measurement.TextValue !== null
        ? measurement.TextValue
        : measurement.textValue;
    const textValue =
      typeof rawText === "string" && rawText.trim() !== ""
        ? rawText.trim()
        : null;

    const point = {
      timestamp: measurement.DateTime,
      time: formatTimeAxis(new Date(measurement.DateTime), selectedPeriod),
      value: parseFloat(measurement.Value),
    };
    if (textValue) {
      point.textValue = textValue;
    }
    return point;
  }).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
};

/**
 * Calculate average, standard deviation, and Z-score from measurement data
 * @param {Array} measurements - Array of measurement objects from API (with Value property) or chart format (with value property)
 * @param {number|null} currentValue - Current value to calculate Z-score for (optional)
 * @returns {Object} Object with average, standardDeviation, and zScore properties
 */
export const calculateAverageAndZScore = (
  measurements,
  currentValue = null
) => {
  if (
    !measurements
    || !Array.isArray(measurements)
    || measurements.length === 0
  ) {
    return { average: null, standardDeviation: null, zScore: null };
  }

  // Extract numeric values, filtering out invalid values
  // API format uses 'Value' (capital V), chart format uses 'value' (lowercase)
  const values = measurements
    .map((m) => {
      if (typeof m !== "object" || m === null) {
        const num =
          typeof m === "string" ? parseFloat(removeCommasAndSpaces(m)) : m;
        return typeof num === "number" && !isNaN(num) && isFinite(num)
          ? num
          : null;
      }
      // Try both Value (API format) and value (chart format)
      const val = m.Value !== undefined ? m.Value : m.value;
      const num =
        typeof val === "string" ? parseFloat(removeCommasAndSpaces(val)) : val;
      return typeof num === "number" && !isNaN(num) && isFinite(num)
        ? num
        : null;
    })
    .filter((v) => v !== null);

  if (values.length === 0) {
    return { average: null, standardDeviation: null, zScore: null };
  }

  // Calculate average
  const sum = values.reduce((acc, val) => acc + val, 0);
  const average = sum / values.length;

  // Calculate standard deviation (population standard deviation)
  const squaredDiffs = values.map((val) => Math.pow(val - average, 2));
  const variance =
    squaredDiffs.reduce((acc, val) => acc + val, 0) / values.length;
  const standardDeviation = Math.sqrt(variance);

  // Calculate Z-score if current value is provided
  let zScore = null;
  if (
    currentValue !== null
    && typeof currentValue === "number"
    && !isNaN(currentValue)
    && isFinite(currentValue)
  ) {
    if (standardDeviation > 0) {
      zScore = (currentValue - average) / standardDeviation;
    } else {
      // If standard deviation is 0 (all values are the same), set Z-score to 0
      zScore = 0;
    }
  }

  return { average, standardDeviation, zScore };
};

/**
 * Filter parameters by selected tag groups (union by UniversalDescriptorId)
 * @param {Array} parameters - Parameter list
 * @param {Array} groups - Selected tag group objects
 * @returns {Array} Filtered parameters matching any selected group descriptor
 */
export const filterParametersByGroups = (parameters, groups) => {
  if (!Array.isArray(parameters) || parameters.length === 0) {
    return [];
  }

  if (!Array.isArray(groups) || groups.length === 0) {
    return parameters;
  }

  const universalDescriptorIds = new Set();

  groups.forEach((group) => {
    if (!Array.isArray(group?.UniversalDescriptors)) {
      return;
    }

    group.UniversalDescriptors.forEach((universalDescriptor) => {
      const universalDescriptorId = Number(
        universalDescriptor?.UniversalDescriptorId
      );
      if (!Number.isNaN(universalDescriptorId)) {
        universalDescriptorIds.add(universalDescriptorId);
      }
    });
  });

  if (universalDescriptorIds.size === 0) {
    return [];
  }

  return parameters.filter((parameter) => {
    if (parameter?.apiData?.source === ENALYSIS_TREND_LIST_SOURCE) {
      return true;
    }
    const universalDescriptorId = Number(
      parameter?.universalDescriptorId || parameter?.UniversalDescriptorId
    );
    return (
      !Number.isNaN(universalDescriptorId)
      && universalDescriptorIds.has(universalDescriptorId)
    );
  });
};

/**
 * Flatten categorized tag data into a single parameter array.
 * @param {Object|null|undefined} tagData
 * @returns {Object[]}
 */
export function flattenTagDataToParameters(tagData) {
  const flatParams = [];
  if (!tagData || typeof tagData !== "object") {
    return flatParams;
  }

  Object.entries(tagData).forEach(([category, parameters]) => {
    if (arrayUtils.hasItems(parameters)) {
      parameters.forEach((param) => {
        flatParams.push({
          ...param,
          category,
        });
      });
    }
  });

  return flatParams;
}
